import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useAuthHydration } from '@/hooks/use-auth-hydration';
import { useMembership } from '@/hooks/use-membership';
import { serviceKeys } from '@/hooks/use-services';
import api from '@/services/api';
import { CatalogItem, CreateCatalogItemDto, UpdateCatalogItemDto } from '@/types/service-catalog';

const BASE = '/provider/me/service-catalog';

export const catalogKeys = {
  all: ['service-catalog'] as const,
  list: () => [...catalogKeys.all, 'list'] as const,
  detail: (id: string) => [...catalogKeys.all, 'detail', id] as const,
};

/** Só existe em equipe: prestador solo não tem catálogo (a API responderia 403). */
export function useServiceCatalog(options?: { enabled?: boolean }) {
  const isHydrated = useAuthHydration();
  const { membership } = useMembership();

  return useQuery({
    queryKey: catalogKeys.list(),
    queryFn: async () => (await api.get<CatalogItem[]>(BASE)).data,
    enabled: isHydrated && membership?.type === 'TEAM' && options?.enabled !== false,
  });
}

export function useCatalogItem(id?: string) {
  return useQuery({
    queryKey: catalogKeys.detail(id ?? ''),
    queryFn: async () => (await api.get<CatalogItem>(`${BASE}/${id}`)).data,
    enabled: !!id,
  });
}

/**
 * Mexer no catálogo muda as cópias (nome, descrição, quem faz), então a lista
 * de serviços do prestador também sai do cache.
 *
 * Sem `await`: a mutação termina quando a API responde, não quando cada lista
 * volta do servidor — senão o switch "eu faço" fica travado esperando a
 * página inteira de serviços recarregar.
 */
function useInvalidateCatalog() {
  const queryClient = useQueryClient();
  return () => {
    void queryClient.invalidateQueries({ queryKey: catalogKeys.all });
    void queryClient.invalidateQueries({ queryKey: serviceKeys.all });
  };
}

/**
 * A API devolve o item já atualizado: escreve no cache na hora, e o refetch
 * das outras listas corre atrás, em segundo plano.
 */
function useApplyCatalogItem() {
  const queryClient = useQueryClient();
  const invalidate = useInvalidateCatalog();
  return (item: CatalogItem) => {
    queryClient.setQueryData(catalogKeys.detail(item.id), item);
    queryClient.setQueryData<CatalogItem[]>(catalogKeys.list(), (old) =>
      old?.map((current) => (current.id === item.id ? item : current)),
    );
    invalidate();
  };
}

export function useCreateCatalogItem() {
  const invalidate = useInvalidateCatalog();

  return useMutation({
    mutationFn: async (data: CreateCatalogItemDto) =>
      (await api.post<CatalogItem>(BASE, data)).data,
    onSuccess: invalidate,
  });
}

export function useUpdateCatalogItem() {
  const apply = useApplyCatalogItem();

  return useMutation({
    mutationFn: async ({ id, ...data }: UpdateCatalogItemDto & { id: string }) =>
      (await api.patch<CatalogItem>(`${BASE}/${id}`, data)).data,
    onSuccess: apply,
  });
}

export function useRemoveCatalogItem() {
  const invalidate = useInvalidateCatalog();

  return useMutation({
    mutationFn: async (id: string) => (await api.delete(`${BASE}/${id}`)).data,
    onSuccess: invalidate,
  });
}

/**
 * Sem `providerIds` é "eu faço": o próprio profissional entra na lista. Com a
 * lista, é o dono dizendo quem faz — e a API barra quem não for dono.
 */
export function useOfferCatalogItem() {
  const apply = useApplyCatalogItem();

  return useMutation({
    mutationFn: async ({ id, providerIds }: { id: string; providerIds?: string[] }) =>
      (await api.post<CatalogItem>(`${BASE}/${id}/offer`, { providerIds })).data,
    onSuccess: apply,
  });
}

export function useUnofferCatalogItem() {
  const apply = useApplyCatalogItem();

  return useMutation({
    mutationFn: async ({ id, providerId }: { id: string; providerId: string }) =>
      (await api.delete<CatalogItem>(`${BASE}/${id}/offer/${providerId}`)).data,
    onSuccess: apply,
  });
}
